import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ShieldCheck, Award, Star, ArrowRight, Activity, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { gsap } from 'gsap';

const Hero = () => {
  const containerRef = useRef(null);
  const badgeRef = useRef(null);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);

  useEffect(() => {
    // Cinematic Floating Animation for Trust Badge
    const ctx = gsap.context(() => {
      gsap.to(badgeRef.current, {
        y: -15,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={containerRef}
      className="relative min-h-[95vh] flex items-center pt-[210px] md:pt-[140px] pb-20 overflow-hidden bg-white"
    >
      {/* Visual Clarity Background */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-navy/[0.02] -skew-x-12 translate-x-1/4 z-0" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-[120px]" />
      
      <div className="section-container relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Content Sanctuary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col gap-10"
          >
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-3">
                <div className="h-[2px] w-12 bg-primary" />
                <span className="text-[11px] font-bold tracking-[0.4em] text-primary uppercase">Elite Veterinary Sanctuary</span>
              </div>
              
              <div className="space-y-4">
                <h1 className="text-navy leading-[1.1] tracking-tight text-[clamp(2rem,10vw,3.75rem)] md:text-5xl lg:text-6xl">
                  Complete Pet Care & <span className="text-primary italic">Advanced Surgical Center</span> in Vanasthalipuram
                </h1>
                <p className="text-[10px] md:text-[14px] font-bold tracking-[0.25em] md:tracking-[0.4em] text-navy/60 uppercase leading-normal">
                  Reliable Veterinary Care & Modern Treatments
                </p>
              </div>
              
              <p className="text-base md:text-lg text-navy/70 font-medium max-w-xl leading-relaxed">
                Providing reliable veterinary care with modern treatments to keep your pets healthy and protected. Directed by Dr. T Suresh Babu.
              </p>
            </div>

             <div className="flex flex-wrap sm:flex-nowrap gap-4 md:gap-6 items-center">
               <a href={`https://wa.me/919848954921?text=${encodeURIComponent("Hi Dr. Suresh, I would like to book an appointment for my pet at your Vanasthalipuram clinic.")}`}
                  target="_blank" rel="noopener noreferrer" 
                  className="w-full sm:w-auto">
                 <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-bold tracking-wider text-[11px] md:text-[14px] px-8 md:px-12 py-5 md:py-6 rounded-full shadow-lg group transition-all duration-300 flex items-center justify-center">
                    Book Appointment <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                 </Button>
               </a>
               <a href="tel:+919848954921" className="group flex items-center justify-center w-full sm:w-auto gap-3 py-4 sm:py-0 transition-colors">
                  <div className="w-8 h-8 md:w-12 md:h-12 rounded-full border border-red-500/20 flex items-center justify-center group-hover:bg-red-500 group-hover:text-white transition-all text-red-500 animate-pulse">
                     <Phone size={14} />
                  </div>
                  <span className="text-[11px] md:text-[13px] font-bold tracking-wide text-navy group-hover:text-red-500 transition-colors flex items-center gap-2">
                     🚨 Emergency Support
                  </span>
               </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 md:gap-8 pt-10 border-t border-navy/5">
               {[
                 { label: 'Surgical Success', value: '99%', icon: <ShieldCheck size={18} className="text-primary" /> },
                 { label: 'Surgeries', value: '500k+', icon: <Activity size={18} className="text-primary" /> },
                 { label: 'Experience', value: '23+ Yrs', icon: <Award size={18} className="text-primary" /> },
               ].map((stat, i) => (
                 <div key={i} className={`flex flex-col gap-1 ${i === 2 ? 'col-span-2 sm:col-span-1 border-t border-navy/5 pt-6 sm:border-t-0 sm:pt-0' : ''}`}>
                   <div className="flex items-center gap-2">
                      {stat.icon}
                      <span className="text-xl md:text-2xl font-extrabold text-navy">{stat.value}</span>
                   </div>
                   <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-wider text-navy/40">{stat.label}</span>
                 </div>
               ))}
            </div>
          </motion.div>

          {/* Visual Sanctuary */}
          <div className="relative">
            <motion.div 
              style={{ y: y1 }}
              className="relative z-20 rounded-[3rem] overflow-hidden shadow-2xl aspect-[4/5] md:aspect-square"
            >
              <img 
                src="/images/doctor.png" 
                alt="Dr. T Suresh Babu Veterinary Sanctuary" 
                className="w-full h-full object-cover object-[95%_top] md:object-[97%_5%] transition-transform duration-[3s] hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
            </motion.div>

            {/* Floating Trust Badge */}
            <motion.div 
              ref={badgeRef}
              className="absolute bottom-2 left-2 md:-bottom-6 md:-left-10 z-30 bg-white/95 backdrop-blur-xl p-3 md:p-8 rounded-[1.5rem] md:rounded-[2.5rem] shadow-glass-heavy border border-white flex flex-col gap-2 md:gap-3 max-w-[110px] md:max-w-[220px]"
            >
               <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white">
                  <Star fill="white" size={18} />
               </div>
               <div className="flex flex-col">
                  <span className="text-navy font-bold text-[10px] md:text-xs uppercase tracking-tight leading-tight mb-1">Certified Surgical Center</span>
                  <div className="flex items-center gap-1">
                     {[...Array(5)].map((_, i) => <Star key={i} size={10} fill="#038903" className="text-primary" />)}
                  </div>
               </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
